import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "@/lib/toast";
import { fmtMoney, setReportingCurrency } from "@/lib/money";
import { formatMoney } from "@/lib/format";
import KpiCard from "@/components/KpiCard";
import { SkeletonCard } from "@/components/Skeleton";
import {
  MessageSquare, Receipt, AlertTriangle, ClipboardCheck, Truck,
  Clock, ChevronRight, Sun, Wallet, FileWarning, TrendingUp,
  CheckCircle2, RefreshCw, ShieldCheck, Percent, Sparkles, Zap,
  PackageX,
} from "lucide-react";

function SeverityDot({ severity }) {
  const cls = severity === "critical" ? "bg-red-500"
            : severity === "high"     ? "bg-amber-500"
            : severity === "medium"   ? "bg-sky-500"
            : "bg-slate-400";
  return <span className={`w-2.5 h-2.5 rounded-full ${cls} shrink-0`} title={severity} />;
}

const STATUS_TONE = {
  ordered: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
  in_process: "bg-sky-50 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300",
  awaiting_arrival: "bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300",
  scheduling: "bg-sky-50 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300",
  scheduled: "bg-sky-50 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300",
  installed: "bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300",
  completed: "bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300",
  cancelled: "bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300",
};

/**
 * State-aware KPI has been unified across the app via <KpiCard />.
 * Zero-values render as neutral navy (informational) instead of celebratory
 * green — a $0 cash balance is not a good outcome.
 */

function SectionCard({ icon: Icon, title, count, testid, children, action, tone = "text-[#C9A356]", collapseWhenEmpty, isEmpty, emptyText }) {
  if (collapseWhenEmpty && isEmpty) {
    // Slim empty-state row instead of a large blank card.
    return (
      <div
        className="card-pb px-5 py-3 flex items-center justify-between gap-2 opacity-80"
        data-testid={testid}
      >
        <div className="inline-flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4 text-emerald-500" />
          <span className="text-sm text-[#0A2540] dark:text-white">{title}</span>
          <span className="text-xs text-[#94A3B8]">— {emptyText}</span>
        </div>
      </div>
    );
  }
  return (
    <div className="card-pb overflow-hidden" data-testid={testid}>
      <div className="px-5 py-3 flex items-center justify-between border-b border-[#E2E8F0] dark:border-slate-700 bg-slate-50/60 dark:bg-slate-900/40">
        <div className="inline-flex items-center gap-2">
          <Icon className={`w-4 h-4 ${tone}`} />
          <h3 className="font-heading font-medium text-[#0A2540] dark:text-white">{title}</h3>
          {typeof count === "number" && (
            <span className="text-xs font-bold bg-slate-200 dark:bg-slate-700 text-[#0A2540] dark:text-white px-2 py-0.5 rounded-full">
              {count}
            </span>
          )}
        </div>
        {action}
      </div>
      <div className="max-h-[380px] overflow-y-auto">{children}</div>
    </div>
  );
}

const APPROVAL_ICON = {
  discount: Percent,
  client_access: ShieldCheck,
  leave: Clock,
  purchase_order: Truck,
  service_request: MessageSquare,
};

function ApprovalRow({ item }) {
  const Icon = APPROVAL_ICON[item.kind] || AlertTriangle;
  return (
    <Link
      to={item.link}
      data-testid={`approval-${item.kind}-${item.id}`}
      className="flex items-center gap-3 px-5 py-2.5 border-b border-[#E2E8F0] dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800/60 transition"
    >
      <span className="w-8 h-8 rounded-lg bg-[#C9A356]/15 grid place-items-center text-[#C9A356] shrink-0">
        <Icon className="w-4 h-4" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-sm text-[#0A2540] dark:text-white truncate">{item.label}</div>
        {item.sub && <div className="text-[11px] text-[#475569] dark:text-slate-400 truncate">{item.sub}</div>}
      </div>
      <ChevronRight className="w-4 h-4 text-[#94A3B8] shrink-0" />
    </Link>
  );
}

function useRelativeTime(iso) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(t);
  }, []);
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const s = Math.max(0, Math.floor((now - then) / 1000));
  if (s < 60) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return new Date(iso).toLocaleDateString();
}

export default function AdminToday() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const r = await api.get("/admin/today");
      setData(r.data);
      if (r.data?.reporting_currency) setReportingCurrency(r.data.reporting_currency);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load Today");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const relTime = useRelativeTime(data?.generated_at);

  const kpiTones = useMemo(() => {
    const k = data?.kpis || {};
    return {
      // Cash is informational — neutral navy regardless of value.
      overdue:
        k.overdue_invoice_count === 0 ? "positive" :
        (k.overdue_invoice_total >= 10000 || k.overdue_invoice_count >= 5) ? "critical" : "attention",
      delayed:
        k.delayed_project_count === 0 ? "positive" :
        k.delayed_project_count >= 3 ? "critical" : "attention",
      unread:
        k.unread_thread_count === 0 ? "positive" : "attention",
    };
  }, [data]);

  if (loading) {
    return (
      <div data-testid="admin-today" className="space-y-6">
        <div>
          <p className="overline">Command Center</p>
          <h1 className="text-3xl md:text-4xl font-heading font-medium text-[#0A2540] dark:text-white mt-1">Today</h1>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
          {[0,1,2,3].map((i) => <SkeletonCard key={i} />)}
        </div>
        <SkeletonCard />
        <SkeletonCard />
      </div>
    );
  }
  if (!data) return null;

  const k = data.kpis || {};
  const cur = data.reporting_currency || "USD";
  const now = new Date();
  const dateLabel = now.toLocaleDateString([], { weekday: "long", month: "long", day: "numeric" });

  const tot = data.tot_snapshot || {};

  return (
    <div data-testid="admin-today" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <p className="overline">Command Center</p>
          <h1 className="text-3xl md:text-4xl font-heading font-medium text-[#0A2540] dark:text-white mt-1">Today</h1>
          <p className="text-sm text-[#475569] dark:text-slate-400 mt-1 flex items-center gap-2 flex-wrap">
            <Sun className="w-4 h-4 text-[#C9A356]" />
            <span>{dateLabel}</span>
            <span className="text-[#94A3B8]">·</span>
            <span>Updated {relTime}</span>
            <button
              onClick={load}
              disabled={refreshing}
              className="text-[#C9A356] hover:text-[#0A2540] dark:hover:text-white transition inline-flex items-center gap-1"
              data-testid="today-refresh"
              aria-label="Refresh"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
            </button>
          </p>
        </div>
      </div>

      {/* KPI strip — mobile 2×2, desktop 4 across */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 pb-reveal-child">
        <KpiCard
          icon={Wallet} tone="neutral" testId="kpi-cash"
          label="Cash on hand"
          value={k.cash_on_hand} money
          sub={`AR ${formatMoney(k.ar_balance, { decimals: 0 })}`}
        />
        <KpiCard
          icon={FileWarning} tone={kpiTones.overdue} testId="kpi-overdue"
          label="Overdue invoices"
          value={k.overdue_invoice_count}
          sub={formatMoney(k.overdue_invoice_total, { decimals: 0 })}
          href={k.overdue_invoice_count > 0 ? "/admin/payments?filter=overdue" : "/admin/payments"}
        />
        <KpiCard
          icon={AlertTriangle} tone={kpiTones.delayed} testId="kpi-delayed"
          label="Delayed projects"
          value={k.delayed_project_count}
          sub={`Installing today: ${k.installing_today_count}`}
          href={k.delayed_project_count > 0 ? "/admin/projects?filter=delayed" : "/admin/projects"}
        />
        <KpiCard
          icon={MessageSquare} tone={kpiTones.unread} testId="kpi-unread"
          label="Unread threads"
          value={k.unread_thread_count}
          sub={`Wages due ${formatMoney(k.wages_payable, { decimals: 0 })}`}
          href="/admin/project-messages"
        />
      </div>

      {/* Priority Exceptions — what needs attention right now */}
      {(data.top_exceptions?.length || 0) > 0 && (
        <section className="card-pb overflow-hidden" data-testid="section-priority">
          <div className="px-5 py-3 flex items-center justify-between border-b border-[#E2E8F0] dark:border-slate-700 bg-slate-50/60 dark:bg-slate-900/40">
            <div className="inline-flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-red-500" />
              <h3 className="font-heading font-medium text-[#0A2540] dark:text-white">Priority attention</h3>
              <span className="text-xs font-bold bg-red-100 text-red-700 px-2 py-0.5 rounded-full">
                {data.top_exceptions.length}
              </span>
            </div>
            {(data.all_exceptions?.length || 0) > data.top_exceptions.length && (
              <span className="text-xs text-[#94A3B8]">Showing top {data.top_exceptions.length} of {data.all_exceptions.length}</span>
            )}
          </div>
          <ul>
            {data.top_exceptions.map((e) => (
              <li key={e.id} data-testid={`exc-${e.id}`}>
                <Link to={e.link || "#"} className="flex items-center gap-3 px-5 py-3 border-b border-[#E2E8F0] dark:border-slate-700 last:border-b-0 hover:bg-slate-50 dark:hover:bg-slate-800/60 transition min-h-[56px]">
                  <SeverityDot severity={e.severity} />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-[#0A2540] dark:text-white truncate">{e.title}</div>
                    {e.sub && <div className="text-[11px] text-[#64748B] truncate">{e.sub}</div>}
                  </div>
                  <ChevronRight className="w-4 h-4 text-[#94A3B8] shrink-0" />
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* AI Analyst Recommendations */}
      {(data.recommendations?.length || 0) > 0 && (
        <section className="card-pb overflow-hidden" data-testid="section-recommendations">
          <div className="px-5 py-3 flex items-center justify-between border-b border-[#E2E8F0] dark:border-slate-700 bg-gradient-to-r from-[#0A2540]/[.03] to-[#C9A356]/[.06] dark:from-slate-900/40 dark:to-slate-900/40">
            <div className="inline-flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-[#C9A356]" />
              <h3 className="font-heading font-medium text-[#0A2540] dark:text-white">AI analyst — recommended actions</h3>
              <span className="text-xs font-bold bg-[#C9A356]/15 text-[#C9A356] px-2 py-0.5 rounded-full">
                {data.recommendations.length}
              </span>
            </div>
            <span className="text-[10px] text-[#94A3B8] hidden md:inline">Advisory only — nothing is executed automatically</span>
          </div>
          <ul>
            {data.recommendations.slice(0, 6).map((r) => (
              <li key={r.id} data-testid={`rec-${r.id}`} className="px-5 py-3 border-b border-[#E2E8F0] dark:border-slate-700 last:border-b-0 flex items-start gap-3">
                <span className="w-8 h-8 rounded-lg bg-[#C9A356]/15 grid place-items-center text-[#C9A356] shrink-0 mt-0.5">
                  <Zap className="w-4 h-4" />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-[#0A2540] dark:text-white">{r.message}</div>
                  {r.sub && <div className="text-[11px] text-[#64748B] mt-0.5">{r.sub}</div>}
                </div>
                {r.action_link && (
                  <Link to={r.action_link} className="shrink-0 px-3 py-1.5 rounded-lg bg-[#0A2540] text-white text-xs font-semibold hover:brightness-110 inline-flex items-center gap-1 min-h-[36px]" data-testid={`rec-action-${r.id}`}>
                    {r.action_label || "Open"} <ChevronRight className="w-3.5 h-3.5" />
                  </Link>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      <SectionCard
        icon={ShieldCheck}
        title="Needs your approval"
        count={data.approvals?.length || 0}
        testid="section-approvals"
        tone="text-[#C9A356]"
        collapseWhenEmpty
        isEmpty={(data.approvals?.length || 0) === 0}
        emptyText="Nothing waiting for review"
      >
        {(data.approvals || []).map((a) => (
          <ApprovalRow key={`${a.kind}-${a.id}`} item={a} />
        ))}
      </SectionCard>

      {/* Two-column grid of action lists */}
      <div className="grid lg:grid-cols-2 gap-6">
        <SectionCard
          icon={MessageSquare}
          title="Unread client messages"
          count={data.unread_threads.length}
          testid="section-unread-threads"
          action={
            <Link to="/admin/project-messages" className="text-xs text-[#C9A356] hover:underline inline-flex items-center gap-0.5">
              Open inbox <ChevronRight className="w-3 h-3" />
            </Link>
          }
          collapseWhenEmpty
          isEmpty={data.unread_threads.length === 0}
          emptyText="Inbox zero"
        >
          {data.unread_threads.map((t) => (
            <Link
              key={t.project_id}
              to={`/admin/project-messages?project=${t.project_id}`}
              data-testid={`today-thread-${t.project_id}`}
              className="block px-5 py-3 border-b border-[#E2E8F0] dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800/60 transition"
            >
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm text-[#0A2540] dark:text-white truncate">
                      {t.client_name || "—"}
                    </span>
                    <span className="min-w-[20px] h-[20px] px-1.5 grid place-items-center rounded-full bg-red-500 text-white text-[10px] font-bold">
                      {t.unread_count}
                    </span>
                  </div>
                  <div className="text-[11px] text-[#475569] dark:text-slate-400 truncate">
                    {t.project_number} · {t.customer_code || ""}
                  </div>
                  {t.last_preview && (
                    <div className="text-xs text-[#0A2540] dark:text-slate-300 truncate mt-1 italic">
                      "{t.last_preview}"
                    </div>
                  )}
                </div>
                <ChevronRight className="w-4 h-4 text-[#94A3B8] shrink-0" />
              </div>
            </Link>
          ))}
        </SectionCard>

        <SectionCard
          icon={Receipt}
          title="Overdue invoices"
          count={data.overdue_invoices.length}
          testid="section-overdue"
          tone={data.overdue_invoices.length > 0 ? "text-red-500" : "text-emerald-500"}
          action={
            <Link to="/admin/payments" className="text-xs text-[#C9A356] hover:underline inline-flex items-center gap-0.5">
              All invoices <ChevronRight className="w-3 h-3" />
            </Link>
          }
          collapseWhenEmpty
          isEmpty={data.overdue_invoices.length === 0}
          emptyText="All caught up"
        >
          {data.overdue_invoices.map((inv) => (
            <Link
              key={inv.invoice_id}
              to={`/admin/invoices/${inv.invoice_id}`}
              data-testid={`today-invoice-${inv.invoice_id}`}
              className="flex items-center justify-between gap-3 px-5 py-3 border-b border-[#E2E8F0] dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800/60 transition"
            >
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-[#0A2540] dark:text-white truncate">
                  {inv.title || inv.invoice_number}
                </div>
                <div className="text-[11px] text-[#475569] dark:text-slate-400 truncate">
                  {inv.client_name || inv.customer_code || "—"} · Due {inv.due_date}
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-sm font-heading font-medium text-red-600">
                  {fmtMoney(inv.amount, inv.currency || cur)}
                </div>
                <div className="text-[10px] uppercase font-bold text-red-500">
                  {inv.days_late}d late
                </div>
              </div>
            </Link>
          ))}
        </SectionCard>

        <SectionCard
          icon={AlertTriangle}
          title="Delayed projects"
          count={data.delayed_projects.length}
          testid="section-delayed"
          tone={data.delayed_projects.length > 0 ? "text-amber-500" : "text-emerald-500"}
          action={
            <Link to="/admin/projects" className="text-xs text-[#C9A356] hover:underline inline-flex items-center gap-0.5">
              All projects <ChevronRight className="w-3 h-3" />
            </Link>
          }
          collapseWhenEmpty
          isEmpty={data.delayed_projects.length === 0}
          emptyText="Every scheduled project is on track"
        >
          {data.delayed_projects.map((p) => (
            <Link
              key={p.project_id}
              to={`/admin/projects/${p.project_id}`}
              data-testid={`today-delayed-${p.project_id}`}
              className="flex items-center justify-between gap-3 px-5 py-3 border-b border-[#E2E8F0] dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800/60 transition"
            >
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-[#0A2540] dark:text-white truncate">{p.title}</div>
                <div className="text-[11px] text-[#475569] dark:text-slate-400 truncate">
                  {p.project_number} · {p.client_name || p.customer_code || "—"}
                </div>
              </div>
              <div className="text-right shrink-0">
                <span className={`inline-block px-1.5 py-0.5 rounded text-[9px] uppercase font-bold ${STATUS_TONE[p.status] || "bg-slate-200 text-slate-700"}`}>
                  {(p.status || "").replace(/_/g, " ")}
                </span>
                <div className="text-[10px] uppercase font-bold text-amber-600 mt-0.5">
                  {p.days_late}d overdue
                </div>
              </div>
            </Link>
          ))}
        </SectionCard>

        <SectionCard
          icon={TrendingUp}
          title="Installing today"
          count={data.installing_today.length}
          testid="section-installing-today"
          tone="text-emerald-500"
          action={
            <Link to="/admin/dispatch" className="text-xs text-[#C9A356] hover:underline inline-flex items-center gap-0.5">
              Dispatch <ChevronRight className="w-3 h-3" />
            </Link>
          }
          collapseWhenEmpty
          isEmpty={data.installing_today.length === 0}
          emptyText="No installs scheduled for today"
        >
          {data.installing_today.map((p) => (
            <Link
              key={p.project_id}
              to={`/admin/projects/${p.project_id}`}
              data-testid={`today-installing-${p.project_id}`}
              className="flex items-center justify-between gap-3 px-5 py-3 border-b border-[#E2E8F0] dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800/60 transition"
            >
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-[#0A2540] dark:text-white truncate">{p.title}</div>
                <div className="text-[11px] text-[#475569] dark:text-slate-400 truncate">
                  {p.project_number} · {p.client_name || p.customer_code || "—"}
                </div>
              </div>
              <span className={`inline-block px-1.5 py-0.5 rounded text-[9px] uppercase font-bold ${STATUS_TONE[p.status] || "bg-slate-200"}`}>
                {(p.status || "").replace(/_/g, " ")}
              </span>
            </Link>
          ))}
        </SectionCard>

        <SectionCard
          icon={ClipboardCheck}
          title="Open punchlist"
          count={data.open_punchlist.length}
          testid="section-punchlist"
          tone="text-purple-500"
          collapseWhenEmpty
          isEmpty={data.open_punchlist.length === 0}
          emptyText="Punchlist clean"
        >
          {data.open_punchlist.map((p) => (
            <Link
              key={p.punch_id}
              to={`/admin/projects/${p.project_id}`}
              data-testid={`today-punch-${p.punch_id}`}
              className="flex items-center justify-between gap-3 px-5 py-3 border-b border-[#E2E8F0] dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800/60 transition"
            >
              <div className="min-w-0 flex-1">
                <div className="text-sm text-[#0A2540] dark:text-white truncate">{p.description}</div>
                <div className="text-[11px] text-[#475569] dark:text-slate-400 truncate">
                  {p.project_number || p.project_id} · {p.project_title}
                </div>
              </div>
              <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${p.priority === "high" ? "bg-red-100 text-red-700" : p.priority === "low" ? "bg-slate-100 text-slate-600" : "bg-amber-100 text-amber-700"}`}>
                {p.priority}
              </span>
            </Link>
          ))}
        </SectionCard>

        <SectionCard
          icon={PackageX}
          title="Reorder alerts"
          count={(data.reorder_alerts || []).length}
          testid="section-reorder"
          tone={(data.reorder_alerts || []).length > 0 ? "text-red-500" : "text-emerald-500"}
          action={
            <Link to="/admin/materials" className="text-xs text-[#C9A356] hover:underline inline-flex items-center gap-0.5">
              Open inventory <ChevronRight className="w-3 h-3" />
            </Link>
          }
          collapseWhenEmpty
          isEmpty={(data.reorder_alerts || []).length === 0}
          emptyText="All stock levels healthy"
        >
          {(data.reorder_alerts || []).map((m) => {
            const badgeTone = m.severity === "critical"
              ? "bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-300"
              : m.severity === "high"
                ? "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300"
                : "bg-sky-100 text-sky-700 dark:bg-sky-500/20 dark:text-sky-300";
            return (
              <Link
                key={m.material_id}
                to="/admin/materials"
                data-testid={`today-reorder-${m.material_id}`}
                className="flex items-center justify-between gap-3 px-5 py-3 border-b border-[#E2E8F0] dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800/60 transition"
              >
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-[#0A2540] dark:text-white truncate">{m.name}</div>
                  <div className="text-[11px] text-[#475569] dark:text-slate-400 truncate">
                    {m.sku ? `SKU ${m.sku} · ` : ""}{Math.round(m.stock)} on hand · reorder ≤ {Math.round(m.reorder_level)}
                  </div>
                </div>
                <span className={`text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full shrink-0 ${badgeTone}`}>
                  {m.stock <= 0 ? "OUT" : m.severity}
                </span>
              </Link>
            );
          })}
        </SectionCard>

        <SectionCard
          icon={Truck}
          title="Purchase orders in transit"
          count={data.purchase_orders_in_transit.length}
          testid="section-po"
          tone="text-blue-500"
          action={
            <Link to="/admin/purchase-orders" className="text-xs text-[#C9A356] hover:underline inline-flex items-center gap-0.5">
              All POs <ChevronRight className="w-3 h-3" />
            </Link>
          }
          collapseWhenEmpty
          isEmpty={data.purchase_orders_in_transit.length === 0}
          emptyText="No POs waiting to be received"
        >
          {data.purchase_orders_in_transit.map((po) => (
            <Link
              key={po.po_id}
              to={`/admin/purchase-orders`}
              data-testid={`today-po-${po.po_id}`}
              className="flex items-center justify-between gap-3 px-5 py-3 border-b border-[#E2E8F0] dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800/60 transition"
            >
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-[#0A2540] dark:text-white truncate">
                  {po.po_number}
                </div>
                <div className="text-[11px] text-[#475569] dark:text-slate-400 truncate">
                  {po.supplier_name || "—"} · ETA {po.expected_date || "—"}
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-sm font-heading text-[#0A2540] dark:text-white">
                  {fmtMoney(po.total, cur)}
                </div>
                {typeof po.days_out === "number" && po.expected_date && (
                  <div className={`text-[10px] uppercase font-bold ${po.days_out < 0 ? "text-red-500" : "text-blue-500"}`}>
                    {po.days_out < 0 ? `${-po.days_out}d late` : `${po.days_out}d out`}
                  </div>
                )}
              </div>
            </Link>
          ))}
        </SectionCard>
      </div>

      {/* TOT snapshot */}
      <Link
        to="/admin/tot-report"
        className="card-pb block p-5 hover:-translate-y-0.5 transition border-l-4 border-[#C9A356]"
        data-testid="section-tot"
      >
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="inline-flex items-center gap-2 mb-1">
              <Percent className="w-4 h-4 text-[#C9A356]" />
              <p className="overline">Turnover tax · {tot.period}</p>
            </div>
            <div className="text-2xl font-heading text-[#0A2540] dark:text-white">
              Estimated {fmtMoney(tot.estimated_tot, tot.currency || cur)}
            </div>
            <p className="text-xs text-[#475569] dark:text-slate-400 mt-1">
              Taxable base {fmtMoney(tot.taxable_base, tot.currency || cur)} × {((tot.rate || 0) * 100).toFixed(0)}%
              · Status: <span className="uppercase font-bold text-amber-600">{(tot.status || "not_filed").replace(/_/g, " ")}</span>
            </p>
          </div>
          <ChevronRight className="w-5 h-5 text-[#94A3B8]" />
        </div>
      </Link>

      {/* Timesheets pending */}
      {k.timesheets_pending > 0 && (
        <Link to="/admin/timesheets" className="card-pb block p-5 border-l-4 border-amber-500 hover:-translate-y-0.5 transition" data-testid="section-timesheets">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="inline-flex items-center gap-2 mb-1">
                <Clock className="w-4 h-4 text-amber-500" />
                <p className="overline">Timesheets pending approval</p>
              </div>
              <div className="text-2xl font-heading text-[#0A2540] dark:text-white">
                {k.timesheets_pending} draft entr{k.timesheets_pending === 1 ? "y" : "ies"}
              </div>
              <p className="text-xs text-[#475569] dark:text-slate-400 mt-1">
                Approve to accrue payroll into the ledger.
              </p>
            </div>
            <ChevronRight className="w-5 h-5 text-[#94A3B8]" />
          </div>
        </Link>
      )}
    </div>
  );
}
